"use client"

import { create } from "zustand"
import { useShallow } from "zustand/react/shallow"
import {
    persist,
    subscribeWithSelector,
    createJSONStorage,
} from "zustand/middleware"
import { immer } from "zustand/middleware/immer"
import { v4 as uuidv4 } from "uuid"
import { toExplorerSection } from "@/components/desktop/windows/explorer/explorer.utils"
import {
    commsDataFromLegacyWindow,
    isLegacyCommsWindowType,
} from "@/components/desktop/windows/comms/commsWindow.utils"
import {
    WindowType,
    WindowState,
    WINDOW_TYPES,
    LEGACY_WINDOW_TYPES,
    RECORD_WINDOW_TYPES,
    type WindowOpenSource,
} from "@/components/desktop/types"
import { WINDOW_REGISTRY_METADATA as WINDOW_REGISTRY } from "@/components/desktop/windowRegistry.metadata"
import { captureAnalyticsEvent } from "@/utils/analytics.utils"

const WINDOW_STATE_VERSION = 3

const MAX_OPEN_RECORDS = 6

export function remapOriginWindowId(
    data: WindowState["data"] | undefined,
    restoredIds: Map<string | undefined, string>,
    relabelOriginIds: ReadonlySet<string> = new Set()
): WindowState["data"] | undefined {
    const originId = data?.originWindowId
    if (typeof originId !== "string") return data
    const next: Record<string, unknown> = {
        ...data,
        originWindowId: restoredIds.get(originId),
    }
    if (relabelOriginIds.has(originId)) delete next.originLabel
    return next
}

export function normalizeLegacyWindowData(
    w: WindowState
): WindowState["data"] | undefined {
    if (w.type !== "template") return w.data
    if (typeof w.data !== "object" || w.data === null) return w.data
    if ("kind" in w.data) return w.data
    return { ...w.data, kind: "pdf" }
}

let currentOrgId: string | null = null
let isOrgTransitioning = false
let initGeneration = 0

const safeLocalStorage = {
    getItem: (key: string): string | null => {
        try {
            return localStorage.getItem(key)
        } catch {
            return null
        }
    },
    setItem: (key: string, value: string): boolean => {
        try {
            localStorage.setItem(key, value)
            return true
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.warn(
                    `[desktopStore] localStorage.setItem failed:`,
                    error
                )
            }
            return false
        }
    },
    removeItem: (key: string): void => {
        try {
            localStorage.removeItem(key)
        } catch {}
    },
}

const createOrgScopedStorage = () => ({
    getItem: (name: string): string | null => {
        if (typeof window === "undefined") return null
        if (!currentOrgId) return null
        return safeLocalStorage.getItem(`${name}-${currentOrgId}`)
    },
    setItem: (name: string, value: string): void => {
        if (typeof window === "undefined") return
        if (!currentOrgId || isOrgTransitioning) {
            return
        }
        safeLocalStorage.setItem(`${name}-${currentOrgId}`, value)
    },
    removeItem: (name: string): void => {
        if (typeof window === "undefined") return
        if (!currentOrgId) return
        safeLocalStorage.removeItem(`${name}-${currentOrgId}`)
    },
})

export const WINDOW_DEFAULTS: Record<WindowType, { title: string }> = {
    explorer: { title: "Explorer" },
    wilson: { title: "Wilson" },
    comms: { title: "Comms" },
    tasks: { title: "Tasks" },
    todos: { title: "Routines" },
    settings: { title: "Settings" },
    document: { title: "Document" },
    setup: { title: "Setup" },
    onboarding: { title: "Workflow Setup" },
    diagramViewer: { title: "Workflow Diagram" },
    reports: { title: "Reports" },
    report: { title: "Report" },
    meetWilson: { title: "Meet Wilson" },
    knowledgeNote: { title: "Knowledge Note" },
    knowledgeDoc: { title: "Reference" },
    template: { title: "Template" },
    warehouseScheduling: { title: "Docks" },
    skill: { title: "Skill" },
    freightDashboard: { title: "Dashboard" },
    cartagePay: { title: "Wilson Wallet" },
}

interface WindowIdentityConfig {
    identityOf: (
        data: Record<string, unknown> | undefined
    ) => string | undefined
    shouldUpdateTitle: boolean
    shouldUpdateData: boolean
}

const WINDOW_IDENTITY: Partial<Record<WindowType, WindowIdentityConfig>> =
    WINDOW_TYPES.reduce<Partial<Record<WindowType, WindowIdentityConfig>>>(
        (acc, type) => {
            const entry = WINDOW_REGISTRY[type]
            if (entry.identityOf) {
                acc[type] = {
                    identityOf: entry.identityOf,
                    shouldUpdateTitle: entry.shouldUpdateTitleOnReveal ?? false,
                    shouldUpdateData: entry.shouldUpdateDataOnReveal ?? false,
                }
            }
            return acc
        },
        {}
    )

const withoutTransientData = (
    win: Pick<WindowState, "type" | "data">
): Record<string, unknown> | undefined => {
    const transientKeys = WINDOW_REGISTRY[win.type].transientDataKeys
    if (!win.data || !transientKeys) return win.data
    return Object.fromEntries(
        Object.entries(win.data).filter(([key]) => !transientKeys.includes(key))
    )
}

export function getStagedWindow(windows: WindowState[]): WindowState | null {
    return windows
        .filter(w => w.displayState === "normal")
        .reduce<WindowState | null>(
            (top, w) => (!top || w.zIndex > top.zIndex ? w : top),
            null
        )
}

export function getStageBackTarget(
    windows: WindowState[],
    fromId: string,
    walkedIds: readonly string[] = []
): WindowState | null {
    const from = windows.find(w => w.id === fromId)
    const originId = from?.data?.originWindowId
    const origin =
        typeof originId === "string" &&
        originId !== fromId &&
        !walkedIds.includes(originId)
            ? windows.find(w => w.id === originId)
            : undefined
    if (origin) return origin
    return windows
        .filter(w => w.displayState === "normal" && w.id !== fromId)
        .reduce<WindowState | null>(
            (top, w) => (!top || w.zIndex > top.zIndex ? w : top),
            null
        )
}

interface DesktopState {
    windows: WindowState[]
    activeWindowId: string | null
    nextZIndex: number
    stageBackWalkIds: string[]

    isWilsonBarOpen: boolean
    wilsonBarPrefill: string | undefined

    _orgId: string | null
    _initialized: boolean
    _rehydrated: boolean
}

function isPartialDesktopState(value: unknown): value is Partial<DesktopState> {
    if (typeof value !== "object" || value === null) return false
    const windows = (value as { windows?: unknown }).windows
    if (windows === undefined) return true
    return (
        Array.isArray(windows) &&
        windows.every(w => typeof w === "object" && w !== null)
    )
}

interface DesktopActions {
    initialize: (orgId: string) => void
    cleanup: () => void

    stageBack: () => void

    openWindow: (
        type: WindowType | (typeof LEGACY_WINDOW_TYPES)[number],
        options?: {
            title?: string
            data?: Record<string, unknown>
            source?: WindowOpenSource
        }
    ) => string
    closeWindow: (id: string) => void
    closeAllWindows: () => void
    showDesktop: () => void
    minimizeWindow: (id: string) => void
    restoreWindow: (id: string) => void
    focusWindow: (id: string) => void
    updateTitle: (id: string, title: string) => void
    updateWindowData: (id: string, data: Record<string, unknown>) => void
    revealWindow: (
        id: string,
        options?: { title?: string; data?: Record<string, unknown> }
    ) => string

    openWilsonBar: (prefill?: string) => void
    setWilsonBarOpen: (open: boolean) => void
    toggleWilsonBar: () => void
}

type DesktopStore = DesktopState & DesktopActions

export const useDesktopStore = create<DesktopStore>()(
    subscribeWithSelector(
        persist(
            immer((set, get) => ({
                windows: [],
                activeWindowId: null,
                nextZIndex: 100,
                stageBackWalkIds: [],
                isWilsonBarOpen: false,
                wilsonBarPrefill: undefined,
                _orgId: null,
                _initialized: false,
                _rehydrated: false,

                initialize: (orgId: string) => {
                    const myGeneration = ++initGeneration

                    isOrgTransitioning = true

                    currentOrgId = orgId

                    set(state => {
                        state._orgId = orgId
                        state._initialized = true
                        state._rehydrated = false
                    })

                    if (typeof window !== "undefined") {
                        safeLocalStorage.removeItem("desktop-store")
                    }

                    if (initGeneration !== myGeneration) {
                        return
                    }

                    useDesktopStore.persist.rehydrate()

                    isOrgTransitioning = false
                },

                cleanup: () => {
                    const myGeneration = ++initGeneration
                    isOrgTransitioning = true
                    currentOrgId = null

                    set(state => {
                        state._initialized = false
                        state._orgId = null
                        state._rehydrated = false
                        state.windows = []
                        state.activeWindowId = null
                        state.nextZIndex = 100
                        state.stageBackWalkIds = []
                        state.isWilsonBarOpen = false
                        state.wilsonBarPrefill = undefined
                    })

                    if (initGeneration !== myGeneration) return
                    isOrgTransitioning = false
                },

                stageBack: () => {
                    const staged = getStagedWindow(get().windows)
                    if (!staged) return
                    const target = getStageBackTarget(
                        get().windows,
                        staged.id,
                        get().stageBackWalkIds
                    )
                    set(state => {
                        const leaving = state.windows.find(
                            w => w.id === staged.id
                        )
                        if (leaving) leaving.displayState = "minimized"
                        state.stageBackWalkIds.push(staged.id)
                        const next = target
                            ? state.windows.find(w => w.id === target.id)
                            : undefined
                        if (next) {
                            next.displayState = "normal"
                            next.zIndex = state.nextZIndex
                            state.nextZIndex++
                        }
                        state.activeWindowId = next?.id ?? null
                    })
                },

                openWindow: (type, options) => {
                    const section = toExplorerSection(type)
                    if (section) {
                        return get().openWindow("explorer", {
                            ...options,
                            data: { section },
                        })
                    }
                    if (isLegacyCommsWindowType(type)) {
                        return get().openWindow("comms", {
                            ...options,
                            data: commsDataFromLegacyWindow({
                                type,
                                data: options?.data,
                            }),
                        })
                    }
                    if (type === "tables") {
                        return get().openWindow("explorer", options)
                    }
                    const { windows, nextZIndex } = get()
                    const resolvedType = type as WindowType
                    const identity = WINDOW_IDENTITY[resolvedType]

                    const emitWindowOpened = (isNew: boolean) =>
                        captureAnalyticsEvent("window_opened", {
                            windowType: resolvedType,
                            source: options?.source,
                            isNew,
                        })

                    if (identity) {
                        const key = identity.identityOf(options?.data)
                        const existingWindow =
                            key !== undefined
                                ? windows.find(
                                      w =>
                                          w.type === resolvedType &&
                                          identity.identityOf(w.data) === key
                                  )
                                : undefined
                        if (existingWindow) {
                            emitWindowOpened(false)
                            return get().revealWindow(existingWindow.id, {
                                title: identity.shouldUpdateTitle
                                    ? options?.title
                                    : undefined,
                                data: identity.shouldUpdateData
                                    ? options?.data
                                    : undefined,
                            })
                        }
                    } else if (resolvedType === "explorer") {
                        const existingExplorer = windows.find(
                            w => w.type === "explorer"
                        )
                        if (existingExplorer) {
                            const retargetData = options?.data
                            if (retargetData) {
                                set(state => {
                                    const win = state.windows.find(
                                        w => w.id === existingExplorer.id
                                    )
                                    if (win) win.data = retargetData
                                })
                            }
                            emitWindowOpened(false)
                            return get().revealWindow(existingExplorer.id)
                        }
                    } else if (resolvedType !== "diagramViewer") {
                        const existingWindow = windows.find(
                            w => w.type === resolvedType
                        )
                        if (existingWindow) {
                            const retargetData = options?.data
                            if (
                                WINDOW_REGISTRY[resolvedType]
                                    .shouldUpdateDataOnReveal &&
                                retargetData
                            ) {
                                set(state => {
                                    const win = state.windows.find(
                                        w => w.id === existingWindow.id
                                    )
                                    if (win) win.data = retargetData
                                })
                            }
                            emitWindowOpened(false)
                            return get().revealWindow(existingWindow.id)
                        }
                    }

                    const id = uuidv4()
                    const defaults = WINDOW_DEFAULTS[resolvedType]

                    const newWindow: WindowState = {
                        id,
                        type: resolvedType,
                        title: options?.title ?? defaults.title,
                        displayState: "normal",
                        zIndex: nextZIndex,
                        data: options?.data,
                    }

                    set(state => {
                        state.stageBackWalkIds = []
                        if (RECORD_WINDOW_TYPES.has(resolvedType)) {
                            const records = state.windows
                                .filter(w => RECORD_WINDOW_TYPES.has(w.type))
                                .sort((a, b) => a.zIndex - b.zIndex)
                            while (records.length >= MAX_OPEN_RECORDS) {
                                const oldest = records.shift()
                                if (!oldest) break
                                state.windows = state.windows.filter(
                                    w => w.id !== oldest.id
                                )
                            }
                        }
                        state.windows.push(newWindow)
                        state.activeWindowId = id
                        state.nextZIndex = nextZIndex + 1
                    })

                    emitWindowOpened(true)
                    return id
                },

                closeWindow: (id: string) => {
                    set(state => {
                        state.windows = state.windows.filter(w => w.id !== id)
                        if (state.activeWindowId === id) {
                            const top = state.windows
                                .filter(w => w.displayState !== "minimized")
                                .reduce<WindowState | null>(
                                    (best, w) =>
                                        !best || w.zIndex > best.zIndex
                                            ? w
                                            : best,
                                    null
                                )
                            state.activeWindowId = top?.id ?? null
                        }
                    })
                },

                closeAllWindows: () =>
                    set(state => {
                        state.windows = []
                        state.activeWindowId = null
                    }),

                showDesktop: () =>
                    set(state => {
                        for (const win of state.windows) {
                            win.displayState = "minimized"
                        }
                        state.activeWindowId = null
                        state.stageBackWalkIds = []
                    }),

                minimizeWindow: (id: string) =>
                    set(state => {
                        const win = state.windows.find(w => w.id === id)
                        if (win) {
                            win.displayState = "minimized"
                        }
                        if (state.activeWindowId === id) {
                            state.activeWindowId = null
                        }
                    }),

                restoreWindow: (id: string) =>
                    set(state => {
                        state.stageBackWalkIds = []
                        const win = state.windows.find(w => w.id === id)
                        if (win) {
                            win.displayState = "normal"
                            win.zIndex = state.nextZIndex
                            state.nextZIndex++
                        }
                        state.activeWindowId = id
                    }),

                focusWindow: (id: string) =>
                    set(state => {
                        state.stageBackWalkIds = []
                        const win = state.windows.find(w => w.id === id)
                        if (win) {
                            win.zIndex = state.nextZIndex
                            state.nextZIndex++
                        }
                        state.activeWindowId = id
                    }),

                updateTitle: (id: string, title: string) =>
                    set(state => {
                        const win = state.windows.find(w => w.id === id)
                        if (win) {
                            win.title = title
                        }
                    }),

                updateWindowData: (id: string, data: Record<string, unknown>) =>
                    set(state => {
                        const win = state.windows.find(w => w.id === id)
                        if (win) {
                            win.data = { ...win.data, ...data }
                        }
                    }),

                revealWindow: (id, options) => {
                    set(state => {
                        state.stageBackWalkIds = []
                        const win = state.windows.find(w => w.id === id)
                        if (win) {
                            if (win.displayState === "minimized") {
                                win.displayState = "normal"
                            }
                            win.zIndex = state.nextZIndex
                            state.nextZIndex++
                            if (options?.title !== undefined) {
                                win.title = options.title
                            }
                            if (options?.data !== undefined) {
                                win.data = { ...win.data, ...options.data }
                            }
                        }
                        state.activeWindowId = id
                    })
                    return id
                },

                openWilsonBar: (prefill?: string) =>
                    set(state => {
                        state.isWilsonBarOpen = true
                        state.wilsonBarPrefill = prefill
                    }),

                setWilsonBarOpen: (isOpen: boolean) =>
                    set(state => {
                        state.isWilsonBarOpen = isOpen
                        if (!isOpen) state.wilsonBarPrefill = undefined
                    }),

                toggleWilsonBar: () =>
                    set(state => {
                        state.isWilsonBarOpen = !state.isWilsonBarOpen
                        if (!state.isWilsonBarOpen)
                            state.wilsonBarPrefill = undefined
                    }),
            })),
            {
                name: "desktop-store",
                version: WINDOW_STATE_VERSION,
                migrate: persisted => persisted,
                skipHydration: true, // boolean-naming-exempt: external contract — zustand persist option
                storage: createJSONStorage(() => createOrgScopedStorage()),
                onRehydrateStorage: () => {
                    return (_state, _error) => {
                        useDesktopStore.setState(state => {
                            state._rehydrated = true
                        })
                    }
                },
                partialize: state => ({
                    windows: state.windows.map(w => ({
                        id: w.id,
                        type: w.type,
                        title: w.title,
                        displayState: w.displayState,
                        zIndex: w.zIndex,
                        data: withoutTransientData(w),
                    })),
                    activeWindowId: state.activeWindowId,
                    nextZIndex: state.nextZIndex,
                }),
                merge: (persistedState, currentState) => {
                    const persisted = isPartialDesktopState(persistedState)
                        ? persistedState
                        : {}

                    const validWindowTypes = new Set([
                        ...WINDOW_TYPES,
                        ...LEGACY_WINDOW_TYPES,
                    ])
                    const hasRestoredComms = (persisted.windows ?? []).some(
                        w => w.type === "comms"
                    )
                    const isExplorerGroupType = (type: string) =>
                        type === "explorer" || type === "tables"
                    const explorerGroup = (persisted.windows ?? []).filter(w =>
                        isExplorerGroupType(w.type)
                    )
                    const isRoutinesGroupType = (type: string) =>
                        type === "todos" || type === "tasks"
                    const routinesGroup = (persisted.windows ?? []).filter(w =>
                        isRoutinesGroupType(w.type)
                    )
                    const isForeground = (w: WindowState) =>
                        w.displayState !== "minimized"
                    const pickGroupWinner = (group: WindowState[]) =>
                        group.length > 0
                            ? group.reduce((best, candidate) => {
                                  if (
                                      isForeground(candidate) !==
                                      isForeground(best)
                                  ) {
                                      return isForeground(candidate)
                                          ? candidate
                                          : best
                                  }
                                  return candidate.zIndex > best.zIndex
                                      ? candidate
                                      : best
                              })
                            : undefined
                    const explorerWinner = pickGroupWinner(explorerGroup)
                    const routinesWinner = pickGroupWinner(routinesGroup)
                    const aliasIdsOf = (
                        group: WindowState[],
                        winner: WindowState | undefined
                    ) =>
                        new Set(
                            group
                                .filter(
                                    w =>
                                        w !== winner &&
                                        typeof w.id === "string" &&
                                        w.id
                                )
                                .map(w => w.id)
                        )
                    const explorerAliasIds = aliasIdsOf(
                        explorerGroup,
                        explorerWinner
                    )
                    const routinesAliasIds = aliasIdsOf(
                        routinesGroup,
                        routinesWinner
                    )
                    const aliasIds = new Set([
                        ...explorerAliasIds,
                        ...routinesAliasIds,
                    ])
                    const typedWindows = (persisted.windows ?? [])
                        .filter(
                            w =>
                                validWindowTypes.has(w.type) &&
                                !toExplorerSection(w.type)
                        )
                        .flatMap(w => {
                            if (isExplorerGroupType(w.type)) {
                                if (w !== explorerWinner) return []
                                return [{ ...w, type: "explorer" as const }]
                            }
                            if (isRoutinesGroupType(w.type)) {
                                if (w !== routinesWinner) return []
                                return [{ ...w, type: "todos" as const }]
                            }
                            if (!isLegacyCommsWindowType(w.type)) return [w]
                            if (hasRestoredComms) return []
                            return [
                                {
                                    ...w,
                                    type: "comms" as const,
                                    data: commsDataFromLegacyWindow({
                                        type: w.type,
                                        data: w.data,
                                    }),
                                },
                            ]
                        })
                        .filter(
                            (w, index, all) =>
                                w.type !== "comms" ||
                                all.findIndex(
                                    other => other.type === "comms"
                                ) === index
                        )
                    const keptRecords = new Set(
                        typedWindows
                            .filter(w => RECORD_WINDOW_TYPES.has(w.type))
                            .sort((a, b) => b.zIndex - a.zIndex)
                            .slice(0, MAX_OPEN_RECORDS)
                    )
                    const validWindows = typedWindows.filter(
                        w =>
                            !RECORD_WINDOW_TYPES.has(w.type) ||
                            keptRecords.has(w)
                    )

                    const freshIds = validWindows.map(() => uuidv4())
                    const restoredIds = new Map<string | undefined, string>(
                        validWindows.flatMap((w, i) =>
                            typeof w.id === "string" && w.id
                                ? [[w.id, freshIds[i] ?? uuidv4()]]
                                : []
                        )
                    )
                    for (const [winner, aliasIdsOfWinner] of [
                        [explorerWinner, explorerAliasIds],
                        [routinesWinner, routinesAliasIds],
                    ] as const) {
                        if (!winner || typeof winner.id !== "string") continue
                        const winnerNewId = restoredIds.get(winner.id)
                        if (!winnerNewId) continue
                        for (const aliasId of aliasIdsOfWinner) {
                            restoredIds.set(aliasId, winnerNewId)
                        }
                    }
                    const restoredWindows: WindowState[] = validWindows.map(
                        (w, i) => ({
                            id: freshIds[i] ?? uuidv4(),
                            type: w.type,
                            title: w.title,
                            displayState:
                                w.displayState === "minimized"
                                    ? "minimized"
                                    : "normal",
                            zIndex: w.zIndex,
                            data: remapOriginWindowId(
                                normalizeLegacyWindowData(w),
                                restoredIds,
                                aliasIds
                            ),
                        })
                    )
                    return {
                        ...currentState,
                        windows: restoredWindows,
                        activeWindowId:
                            getStagedWindow(restoredWindows)?.id ?? null,
                        nextZIndex:
                            persisted.nextZIndex ?? currentState.nextZIndex,
                    }
                },
            }
        )
    )
)

export const useWindows = () => useDesktopStore(state => state.windows)

export const useActiveWindowId = () =>
    useDesktopStore(state => state.activeWindowId)

export const useWilsonBarState = () =>
    useDesktopStore(
        useShallow(state => ({
            isWilsonBarOpen: state.isWilsonBarOpen,
            wilsonBarPrefill: state.wilsonBarPrefill,
            openWilsonBar: state.openWilsonBar,
            setWilsonBarOpen: state.setWilsonBarOpen,
            toggleWilsonBar: state.toggleWilsonBar,
        }))
    )

export const useDesktopActions = () =>
    useDesktopStore(
        useShallow(state => ({
            stageBack: state.stageBack,
            openWindow: state.openWindow,
            closeWindow: state.closeWindow,
            closeAllWindows: state.closeAllWindows,
            showDesktop: state.showDesktop,
            minimizeWindow: state.minimizeWindow,
            restoreWindow: state.restoreWindow,
            focusWindow: state.focusWindow,
            updateTitle: state.updateTitle,
            updateWindowData: state.updateWindowData,
        }))
    )

export const useDesktopInitialize = () =>
    useDesktopStore(state => state.initialize)

export const useIsRehydrated = () => useDesktopStore(state => state._rehydrated)
